"use client";

import React from "react";
import { Badge } from "@/subframe/components/Badge";
import { Button } from "@/subframe/components/Button";
import { Calendar } from "@/subframe/components/Calendar";
import { CategoryLabel } from "@/subframe/components/CategoryLabel";
import { IconWithBackground } from "@/subframe/components/IconWithBackground";
import { Select } from "@/subframe/components/Select";
// import { Switch } from "@/subframe/components/Switch";
import { ToggleGroup } from "@/subframe/components/ToggleGroup";
import { Tooltip } from "@/subframe/components/Tooltip";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import * as SubframeCore from "@subframe/core";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchLabels } from "../../redux/reducers/authSlice";

interface ScheduleTemplateMenuProps {
  open: boolean;
  setIsScheduleMessagePopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onScheduleSubmit: (schedulePayload: SchedulePayload) => void;
}

export interface SchedulePayload {
  is_schedule: true;
  schedule_type: "relative" | "specific";
  unit_of_time_name?: string;
  unit_of_time_value?: string;
  schedule_datetime_date?: string;
  time_zone?: string;
  condition_one: string | null;
  condition_two: {
    c?: string;
    v?: string[];
  };
}

const WHATSAPP_TIMEZONES = [
  { name: "Pacific/Niue (GMT-11)", value: "Pacific/Niue" },
  { name: "Pacific/Pago_Pago (GMT-11)", value: "Pacific/Pago_Pago" },
  { name: "Pacific/Honolulu (GMT-10)", value: "Pacific/Honolulu" },
  { name: "Pacific/Tahiti (GMT-10)", value: "Pacific/Tahiti" },
  { name: "Pacific/Marquesas (GMT-9:30)", value: "Pacific/Marquesas" },
  { name: "America/Anchorage (GMT-9)", value: "America/Anchorage" },
  { name: "Pacific/Gambier (GMT-9)", value: "Pacific/Gambier" },
  { name: "America/Los_Angeles (GMT-8)", value: "America/Los_Angeles" },
  { name: "America/Vancouver (GMT-8)", value: "America/Vancouver" },
  { name: "America/Denver (GMT-7)", value: "America/Denver" },
  { name: "America/Phoenix (GMT-7)", value: "America/Phoenix" },
  { name: "America/Chicago (GMT-6)", value: "America/Chicago" },
  { name: "America/Mexico_City (GMT-6)", value: "America/Mexico_City" },
  { name: "America/New_York (GMT-5)", value: "America/New_York" },
  { name: "America/Toronto (GMT-5)", value: "America/Toronto" },
  { name: "America/Halifax (GMT-4)", value: "America/Halifax" },
  { name: "America/Caracas (GMT-4)", value: "America/Caracas" },
  { name: "America/St_Johns (GMT-3:30)", value: "America/St_Johns" },
  { name: "America/Sao_Paulo (GMT-3)", value: "America/Sao_Paulo" },
  { name: "America/Argentina/Buenos_Aires (GMT-3)", value: "America/Argentina/Buenos_Aires" },
  { name: "Atlantic/South_Georgia (GMT-2)", value: "Atlantic/South_Georgia" },
  { name: "Atlantic/Azores (GMT-1)", value: "Atlantic/Azores" },
  { name: "Europe/London (GMT+0)", value: "Europe/London" },
  { name: "Africa/Casablanca (GMT+0)", value: "Africa/Casablanca" },
  { name: "Europe/Paris (GMT+1)", value: "Europe/Paris" },
  { name: "Europe/Berlin (GMT+1)", value: "Europe/Berlin" },
  { name: "Europe/Athens (GMT+2)", value: "Europe/Athens" },
  { name: "Africa/Cairo (GMT+2)", value: "Africa/Cairo" },
  { name: "Europe/Moscow (GMT+3)", value: "Europe/Moscow" },
  { name: "Africa/Nairobi (GMT+3)", value: "Africa/Nairobi" },
  { name: "Asia/Tehran (GMT+3:30)", value: "Asia/Tehran" },
  { name: "Asia/Dubai (GMT+4)", value: "Asia/Dubai" },
  { name: "Europe/Samara (GMT+4)", value: "Europe/Samara" },
  { name: "Asia/Kabul (GMT+4:30)", value: "Asia/Kabul" },
  { name: "Asia/Karachi (GMT+5)", value: "Asia/Karachi" },
  { name: "Asia/Tashkent (GMT+5)", value: "Asia/Tashkent" },
  { name: "Asia/Kolkata (GMT+5:30)", value: "Asia/Kolkata" },
  { name: "Asia/Kathmandu (GMT+5:45)", value: "Asia/Kathmandu" },
  { name: "Asia/Dhaka (GMT+6)", value: "Asia/Dhaka" },
  { name: "Asia/Almaty (GMT+6)", value: "Asia/Almaty" },
  { name: "Asia/Yangon (GMT+6:30)", value: "Asia/Yangon" },
  { name: "Asia/Bangkok (GMT+7)", value: "Asia/Bangkok" },
  { name: "Asia/Jakarta (GMT+7)", value: "Asia/Jakarta" },
  { name: "Asia/Shanghai (GMT+8)", value: "Asia/Shanghai" },
  { name: "Asia/Singapore (GMT+8)", value: "Asia/Singapore" },
  { name: "Asia/Tokyo (GMT+9)", value: "Asia/Tokyo" },
  { name: "Asia/Seoul (GMT+9)", value: "Asia/Seoul" },
  { name: "Australia/Adelaide (GMT+9:30)", value: "Australia/Adelaide" },
  { name: "Australia/Darwin (GMT+9:30)", value: "Australia/Darwin" },
  { name: "Australia/Sydney (GMT+10)", value: "Australia/Sydney" },
  { name: "Pacific/Port_Moresby (GMT+10)", value: "Pacific/Port_Moresby" },
  { name: "Australia/Lord_Howe (GMT+10:30)", value: "Australia/Lord_Howe" },
  { name: "Pacific/Noumea (GMT+11)", value: "Pacific/Noumea" },
  { name: "Asia/Vladivostok (GMT+11)", value: "Asia/Vladivostok" },
  { name: "Pacific/Auckland (GMT+12)", value: "Pacific/Auckland" },
  { name: "Pacific/Fiji (GMT+12)", value: "Pacific/Fiji" },
  { name: "Pacific/Chatham (GMT+12:45)", value: "Pacific/Chatham" },
  { name: "Pacific/Tongatapu (GMT+13)", value: "Pacific/Tongatapu" },
  { name: "Pacific/Apia (GMT+13)", value: "Pacific/Apia" },
  { name: "Pacific/Kiritimati (GMT+14)", value: "Pacific/Kiritimati" },
];

const CONDITION_ONE_ITEM_CLASS =
  "group/condition-one-option relative h-auto max-w-full min-w-0 items-start gap-0 overflow-hidden border-b border-solid border-neutral-100 py-3 pr-8 last:border-b-0 group-data-[state=checked]/condition-one-option:h-8 group-data-[state=checked]/condition-one-option:items-center group-data-[state=checked]/condition-one-option:py-0 group-data-[state=checked]/condition-one-option:[&_.condition-one-option-desc]:hidden group-data-[state=checked]/condition-one-option:[&_.condition-one-option-title]:line-clamp-1 group-data-[state=checked]/condition-one-option:[&_.condition-one-option-title]:text-body group-data-[state=checked]/condition-one-option:[&_.condition-one-option-title]:font-body group-data-[state=checked]/condition-one-option:[&>*:last-child]:top-1/2 group-data-[state=checked]/condition-one-option:[&>*:last-child]:-translate-y-1/2 [&>*:first-child]:block [&>*:first-child]:w-full [&>*:first-child]:min-w-0 [&>*:first-child]:max-w-full [&>*:first-child]:whitespace-normal [&>span>span]:block [&>span>span]:w-full [&>span>span]:min-w-0 [&>*:last-child]:absolute [&>*:last-child]:right-3 [&>*:last-child]:top-3 [&>*:last-child]:z-10 [&>*:last-child]:shrink-0";

function ScheduleMessage({
  open,
  setIsScheduleMessagePopupOpen,
  onScheduleSubmit,
}: ScheduleTemplateMenuProps) {
  const dispatch = useDispatch<AppDispatch>();
  const labels = useSelector((state: RootState) => state.auth.labels);
  const [schedulingType, setSchedulingType] = React.useState<"relative" | "specific">("relative");
  const [isDatePopoverOpen, setIsDatePopoverOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [time_zone, setTimeZone] = React.useState<string>(WHATSAPP_TIMEZONES[0].value);
  const [selectedHour, setSelectedHour] = React.useState<string>("12");
  const [selectedMinute, setSelectedMinute] = React.useState<string>("00");
  const [selectedAmPm, setSelectedAmPm] = React.useState<"AM" | "PM">("AM");
  const schedule_datetime_date = React.useRef<string>("");
  const [selectedConditionLabels, setSelectedConditionLabels] = React.useState<string[]>([]);
  const [unitOfTimeName, setUnitOfTimeName] = React.useState<string>("min");
  const [unitOfTimeValue, setUnitOfTimeValue] = React.useState<string>("1");
  const [conditionOne, setConditionOne] = React.useState<string>("");
  const [conditionTwoOperator, setConditionTwoOperator] = React.useState<string>("include");

  React.useEffect(() => {
    if (open) {
      dispatch(fetchLabels());
    }
  }, [open, dispatch]);

  React.useEffect(() => {
    if (!open) return;
    const now = new Date();
    const hour24 = now.getHours();
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const amPm: "AM" | "PM" = hour24 >= 12 ? "PM" : "AM";
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneExists = WHATSAPP_TIMEZONES.some((timezone) => timezone.value === browserTimezone);
    setSelectedDate(now);
    setSelectedHour(String(hour12).padStart(2, "0"));
    setSelectedMinute(String(now.getMinutes()).padStart(2, "0"));
    setSelectedAmPm(amPm);
    if (timezoneExists) {
      setTimeZone(browserTimezone);
    } else {
      setTimeZone(WHATSAPP_TIMEZONES[0].value);
    }
  }, [open]);

  React.useEffect(() => {
    const parsedHour = parseInt(selectedHour, 10);
    const parsedMinute = parseInt(selectedMinute, 10);
    let hour24 = parsedHour % 12;
    if (selectedAmPm === "PM") {
      hour24 += 12;
    }
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const hour = String(hour24).padStart(2, "0");
    const minute = String(parsedMinute).padStart(2, "0");
    schedule_datetime_date.current = `${year}-${month}-${day}T${hour}:${minute}:00Z`;
  }, [selectedDate, selectedHour, selectedMinute, selectedAmPm]);

  const toggleConditionLabel = (labelName: string) => {
    setSelectedConditionLabels((prev) =>
      prev.includes(labelName)
        ? prev.filter((label) => label !== labelName)
        : [...prev, labelName]
    );
  };

  const availableLabels = labels.filter(
    (label) => !selectedConditionLabels.includes(label.label)
  );

  const handleScheduleSubmit = () => {
    const schedulePayload: SchedulePayload = {
      is_schedule: true,
      schedule_type: schedulingType,
      ...(schedulingType === "relative"
        ? {
            unit_of_time_name: unitOfTimeName,
            unit_of_time_value: unitOfTimeValue,
          }
        : {
            schedule_datetime_date: schedule_datetime_date.current,
            time_zone,
          }),
      condition_one: conditionOne || null,
      condition_two:
        selectedConditionLabels.length > 0
          ? {
              c: conditionTwoOperator,
              v: selectedConditionLabels,
            }
          : {},
    };
    onScheduleSubmit(schedulePayload);
    setIsScheduleMessagePopupOpen(false);
  };

  return (
    <DialogLayout open={open} onOpenChange={(open) => setIsScheduleMessagePopupOpen(open)}>
      <div className="flex max-h-[768px] flex-col items-start rounded-md shadow-lg mobile:h-192 mobile:w-auto mobile:max-w-[384px]">
        <div className="flex w-full flex-col items-start gap-1 border-b border-solid border-neutral-border px-4 py-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Schedule Message
          </span>
          <span className="text-body font-body text-subtext-color">
            Choose when this message should be sent and add conditions.
          </span>
        </div>
        <div className="flex w-full flex-col items-start gap-4 px-6 py-4 overflow-auto">
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-center gap-2">
              <IconWithBackground size="medium" icon="FeatherCalendar" />
              <span className="text-heading-3 font-heading-3 text-default-font">
                Date &amp; Time
              </span>
            </div>
            <div className="flex w-full flex-col items-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
              <div className="flex w-full items-center justify-between">
                <span className="text-body-bold font-body-bold text-default-font">
                  Scheduling Type
                </span>
                <ToggleGroup value={schedulingType} onValueChange={(value) => {
                  if (value === "relative" || value === "specific") {
                    setSchedulingType(value);
                  }
                }}>
                  <ToggleGroup.Item icon={null} value="relative">
                    Relative
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon={null} value="specific">
                    Specific
                  </ToggleGroup.Item>
                </ToggleGroup>
              </div>
              {schedulingType === "relative" && (
                <div className="flex w-full flex-col items-start gap-3">
                  <div className="flex w-full flex-col items-start gap-1">
                    <span className="text-caption-bold font-caption-bold text-default-font mobile:h-auto mobile:w-full mobile:flex-none">
                      Unit of time
                    </span>
                    <Select
                      className="h-auto w-full flex-none"
                      label=""
                      placeholder="Select unit"
                      helpText=""
                      value={unitOfTimeName}
                      onValueChange={(value) => setUnitOfTimeName(value)}
                    >
                      <Select.Item value="min">Minutes</Select.Item>
                      <Select.Item value="hours">Hours</Select.Item>
                      <Select.Item value="days">Days</Select.Item>
                    </Select>
                    <span className="text-caption font-caption text-subtext-color">
                      Choose if you want to schedule in minutes, hours or days
                      from now
                    </span>
                  </div>
                  <div className="flex w-full flex-col items-start gap-1">
                    <span className="text-caption-bold font-caption-bold text-default-font mobile:h-auto mobile:w-full mobile:flex-none">
                      Number
                    </span>
                    <Select
                      className="h-auto w-full flex-none"
                      label=""
                      placeholder="Select number"
                      helpText=""
                      value={unitOfTimeValue}
                      onValueChange={(value) => setUnitOfTimeValue(value)}
                    >
                      <Select.Item value="1">1</Select.Item>
                      <Select.Item value="2">2</Select.Item>
                      <Select.Item value="3">3</Select.Item>
                      <Select.Item value="4">4</Select.Item>
                      <Select.Item value="5">5</Select.Item>
                      <Select.Item value="10">10</Select.Item>
                      <Select.Item value="15">15</Select.Item>
                      <Select.Item value="20">20</Select.Item>
                      <Select.Item value="30">30</Select.Item>
                      <Select.Item value="60">60</Select.Item>
                      <Select.Item value="90">90</Select.Item>
                      <Select.Item value="120">120</Select.Item>
                    </Select>
                    <span className="text-caption font-caption text-subtext-color">
                      Choose in how much time you want the message to get sent
                    </span>
                  </div>
                </div>
              )}
              {schedulingType === "specific" && (
                <div className="flex w-full flex-col items-center gap-3">
                <div className="flex w-full flex-col items-start justify-center gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    TImezone
                  </span>
                  <div className="flex w-full items-center gap-2">
                    <Select
                      className="h-auto grow shrink-0 basis-0"
                      label=""
                      placeholder="Select TImezone"
                      helpText=""
                      icon="FeatherGlobe"
                      value={time_zone}
                      onValueChange={(value) => setTimeZone(value)}
                    >
                      {WHATSAPP_TIMEZONES.map((timezone) => (
                        <Select.Item key={timezone.value} value={timezone.value}>
                          {timezone.name}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="flex w-full items-start gap-4">
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      Date
                    </span>
                    <SubframeCore.Popover.Root open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          variant="neutral-secondary"
                          icon="FeatherCalendar"
                          iconRight="FeatherChevronDown"
                        >
                          {selectedDate.toLocaleDateString()}
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        asChild={true}
                      >
                        <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
                          <Calendar
                            mode={"single"}
                            selected={selectedDate}
                            onSelect={(date: Date | undefined) => {
                              if (date) {
                                setSelectedDate(date);
                                setIsDatePopoverOpen(false);
                              }
                            }}
                            initialFocus
                          />
                        </div>
                      </SubframeCore.Popover.Content>
                    </SubframeCore.Popover.Root>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      Time
                    </span>
                    <div className="flex w-full items-center gap-2">
                      <Select
                        className="h-auto grow shrink-0 basis-0"
                        label=""
                        placeholder="Hour"
                        helpText=""
                        value={selectedHour}
                        onValueChange={(value) => setSelectedHour(value)}
                      >
                        <Select.Item value="01">01</Select.Item>
                        <Select.Item value="02">02</Select.Item>
                        <Select.Item value="03">03</Select.Item>
                        <Select.Item value="04">04</Select.Item>
                        <Select.Item value="05">05</Select.Item>
                        <Select.Item value="06">06</Select.Item>
                        <Select.Item value="07">07</Select.Item>
                        <Select.Item value="08">08</Select.Item>
                        <Select.Item value="09">09</Select.Item>
                        <Select.Item value="10">10</Select.Item>
                        <Select.Item value="11">11</Select.Item>
                        <Select.Item value="12">12</Select.Item>
                      </Select>
                      <Select
                        className="h-auto grow shrink-0 basis-0"
                        label=""
                        placeholder="Minute"
                        helpText=""
                        value={selectedMinute}
                        onValueChange={(value) => setSelectedMinute(value)}
                      >
                        <Select.Item value="00">00</Select.Item>
                        <Select.Item value="01">01</Select.Item>
                        <Select.Item value="02">02</Select.Item>
                        <Select.Item value="03">03</Select.Item>
                        <Select.Item value="04">04</Select.Item>
                        <Select.Item value="05">05</Select.Item>
                        <Select.Item value="06">06</Select.Item>
                        <Select.Item value="07">07</Select.Item>
                        <Select.Item value="08">08</Select.Item>
                        <Select.Item value="09">09</Select.Item>
                        <Select.Item value="10">10</Select.Item>
                        <Select.Item value="11">11</Select.Item>
                        <Select.Item value="12">12</Select.Item>
                        <Select.Item value="13">13</Select.Item>
                        <Select.Item value="14">14</Select.Item>
                        <Select.Item value="15">15</Select.Item>
                        <Select.Item value="16">16</Select.Item>
                        <Select.Item value="17">17</Select.Item>
                        <Select.Item value="18">18</Select.Item>
                        <Select.Item value="19">19</Select.Item>
                        <Select.Item value="20">20</Select.Item>
                        <Select.Item value="21">21</Select.Item>
                        <Select.Item value="22">22</Select.Item>
                        <Select.Item value="23">23</Select.Item>
                        <Select.Item value="24">24</Select.Item>
                        <Select.Item value="25">25</Select.Item>
                        <Select.Item value="26">26</Select.Item>
                        <Select.Item value="27">27</Select.Item>
                        <Select.Item value="28">28</Select.Item>
                        <Select.Item value="29">29</Select.Item>
                        <Select.Item value="30">30</Select.Item>
                        <Select.Item value="31">31</Select.Item>
                        <Select.Item value="32">32</Select.Item>
                        <Select.Item value="33">33</Select.Item>
                        <Select.Item value="34">34</Select.Item>
                        <Select.Item value="35">35</Select.Item>
                        <Select.Item value="36">36</Select.Item>
                        <Select.Item value="37">37</Select.Item>
                        <Select.Item value="38">38</Select.Item>
                        <Select.Item value="39">39</Select.Item>
                        <Select.Item value="40">40</Select.Item>
                        <Select.Item value="41">41</Select.Item>
                        <Select.Item value="42">42</Select.Item>
                        <Select.Item value="43">43</Select.Item>
                        <Select.Item value="44">44</Select.Item>
                        <Select.Item value="45">45</Select.Item>
                        <Select.Item value="46">46</Select.Item>
                        <Select.Item value="47">47</Select.Item>
                        <Select.Item value="48">48</Select.Item>
                        <Select.Item value="49">49</Select.Item>
                        <Select.Item value="50">50</Select.Item>
                        <Select.Item value="51">51</Select.Item>
                        <Select.Item value="52">52</Select.Item>
                        <Select.Item value="53">53</Select.Item>
                        <Select.Item value="54">54</Select.Item>
                        <Select.Item value="55">55</Select.Item>
                        <Select.Item value="56">56</Select.Item>
                        <Select.Item value="57">57</Select.Item>
                        <Select.Item value="58">58</Select.Item>
                        <Select.Item value="59">59</Select.Item>
                      </Select>
                      <Select
                        label=""
                        placeholder="AM/PM"
                        helpText=""
                        value={selectedAmPm}
                        onValueChange={(value) => setSelectedAmPm(value as "AM" | "PM")}
                      >
                        <Select.Item value="AM">AM</Select.Item>
                        <Select.Item value="PM">PM</Select.Item>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-center gap-2">
              <IconWithBackground size="medium" icon="FeatherGitBranch" />
              <div className="flex grow shrink-0 basis-0 flex-col items-start">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Conditions
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Message will only send if conditions are met
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-start gap-1.5 rounded-md border border-solid border-blue-100 bg-blue-50 px-3 py-2.5">
              <div className="flex items-center gap-1.5">
                <SubframeCore.Icon
                  className="flex-none text-caption font-caption text-blue-500"
                  name="FeatherInfo"
                />
                <span className="text-caption-bold font-caption-bold text-blue-700">
                  How conditions work
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-1 pl-0.5">
                <span className="text-caption font-caption text-subtext-color">
                  • Both conditions are <span className="font-semibold text-default-font">optional</span> — only set what you need.
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  • If <span className="font-semibold text-default-font">only one</span> is set, the message sends when that condition is met.
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  • If <span className="font-semibold text-default-font">both</span> are set, <span className="font-semibold text-default-font">Reply Status</span> is checked first, then <span className="font-semibold text-default-font">Label Status</span> — both must match.
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-start gap-3">
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-md bg-brand-50 px-2 py-2">
                      <SubframeCore.Icon
                        className="text-body font-body text-brand-700"
                        name="FeatherMessageCircle"
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="flex items-center gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Recipient Reply Status
                          </span>
                          <SubframeCore.Tooltip.Provider>
                            <SubframeCore.Tooltip.Root>
                              <SubframeCore.Tooltip.Trigger asChild={true}>
                                <SubframeCore.Icon
                                  className="text-caption font-caption text-neutral-400"
                                  name="FeatherInfo"
                                />
                              </SubframeCore.Tooltip.Trigger>
                              <SubframeCore.Tooltip.Portal>
                                <SubframeCore.Tooltip.Content
                                  side="top"
                                  align="center"
                                  sideOffset={4}
                                  asChild={true}
                                >
                                  <Tooltip>
                                    Message will only send if the selected reply
                                    condition is met
                                  </Tooltip>
                                </SubframeCore.Tooltip.Content>
                              </SubframeCore.Tooltip.Portal>
                            </SubframeCore.Tooltip.Root>
                          </SubframeCore.Tooltip.Provider>
                        </div>
                        <Badge
                          className="h-5 w-auto flex-none"
                          variant="neutral"
                        >
                          Optional
                        </Badge>
                      </div>
                      <span className="text-caption font-caption text-subtext-color">
                        Check if user hasn&#39;t replied recently
                      </span>
                    </div>
                  </div>
                  {/* <Switch
                    checked={false}
                    onCheckedChange={() => {}}
                  /> */}
                </div>
                <div className="flex w-full min-w-0 flex-col items-start gap-2 pl-12">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Send only if...
                  </span>
                  <div className="w-full min-w-0 max-w-full">
                  <Select
                    className="condition-one-select h-auto w-full min-w-0 max-w-full flex-none [&>div.border-neutral-border]:h-auto [&>div.border-neutral-border]:min-h-8 [&>div.border-neutral-border]:py-1 [&>div.border-neutral-border_.condition-one-option-desc]:hidden [&>div.border-neutral-border_.condition-one-option-title]:truncate [&>div.border-neutral-border_.condition-one-option-title]:text-body [&>div.border-neutral-border_.condition-one-option-title]:font-body [&>div.border-neutral-border_[data-radix-select-value]]:min-w-0 [&>div.border-neutral-border_[data-radix-select-value]]:w-full [&>div.border-neutral-border_[data-radix-select-value]]:truncate"
                    label=""
                    placeholder="Select condition"
                    helpText=""
                    value={conditionOne || undefined}
                    onValueChange={(value) => setConditionOne(value)}
                  >
                    <Select.Item
                      value="20m"
                      textValue="Recipient did not reply within 20 minutes after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 20 minutes after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 20 minutes after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="45m"
                      textValue="Recipient did not reply within 45 minutes after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 45 minutes after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 45 minutes after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="3h"
                      textValue="Recipient did not reply within 3 hours after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 3 hours after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 3 hours after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="6h"
                      textValue="Recipient did not reply within 6 hours after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 6 hours after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 6 hours after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="12h"
                      textValue="Recipient did not reply within 12 hours after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 12 hours after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 12 hours after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="24h"
                      textValue="Recipient did not reply within 24 hours after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 24 hours after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 24 hours after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="48h"
                      textValue="Recipient did not reply within 48 hours after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 48 hours after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 48 hours after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="72h"
                      textValue="Recipient did not reply within 72 hours after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply within 72 hours after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient did not reply within 72 hours after my last message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="never"
                      textValue="Recipient did not reply after my last message"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient did not reply after my last message
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient has not replied since my most recent message.
                        </span>
                      </span>
                    </Select.Item>
                    <Select.Item
                      value="never-replied"
                      textValue="Recipient never replied to any of my messages"
                      className={CONDITION_ONE_ITEM_CLASS}
                    >
                      <span className="condition-one-option-content flex w-full min-w-0 max-w-full flex-col items-start gap-1">
                        <span className="condition-one-option-title line-clamp-2 w-full max-w-full break-words text-caption-bold font-caption-bold leading-snug text-default-font">
                          Recipient never replied to any of my messages
                        </span>
                        <span className="condition-one-option-desc line-clamp-2 w-full max-w-full break-words text-[11px] font-normal leading-4 text-subtext-color">
                          Send the scheduled message at the schedule time only if
                          <br />
                          the recipient has never replied to any of my messages.
                        </span>
                      </span>
                    </Select.Item>
                  </Select>
                  </div>
                  <div className="flex w-full max-w-[384px] flex-col items-start gap-2 rounded-md border border-solid border-brand-100 bg-brand-50 pl-4 pr-3 py-3">
                    <div className="flex w-full items-center gap-2">
                      <SubframeCore.Icon
                        className="text-heading-3 font-heading-3 text-brand-800"
                        name="FeatherInfo"
                      />
                      <div className="flex grow shrink-0 basis-0 flex-col items-start">
                        <span className="w-full whitespace-pre-wrap text-caption font-caption text-brand-800">
                          {
                            "Message will only send if the recipient has not replied within the selected time period."
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-md bg-neutral-100 px-2 py-2">
                      <SubframeCore.Icon
                        className="text-body font-body text-default-font"
                        name="FeatherTag"
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="flex items-center gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Label Status
                          </span>
                          <SubframeCore.Tooltip.Provider>
                            <SubframeCore.Tooltip.Root>
                              <SubframeCore.Tooltip.Trigger asChild={true}>
                                <SubframeCore.Icon
                                  className="text-caption font-caption text-neutral-400"
                                  name="FeatherInfo"
                                />
                              </SubframeCore.Tooltip.Trigger>
                              <SubframeCore.Tooltip.Portal>
                                <SubframeCore.Tooltip.Content
                                  side="top"
                                  align="center"
                                  sideOffset={4}
                                  asChild={true}
                                >
                                  <Tooltip>
                                    Control when to send based on conversation
                                    labels
                                  </Tooltip>
                                </SubframeCore.Tooltip.Content>
                              </SubframeCore.Tooltip.Portal>
                            </SubframeCore.Tooltip.Root>
                          </SubframeCore.Tooltip.Provider>
                        </div>
                        <Badge
                          className="h-5 w-auto flex-none"
                          variant="neutral"
                        >
                          Optional
                        </Badge>
                      </div>
                      <span className="text-caption font-caption text-subtext-color">
                        Check conversation labels
                      </span>
                    </div>
                  </div>
                  {/* <Switch
                    checked={false}
                    onCheckedChange={() => {}}
                  /> */}
                </div>
                <div className="flex w-full flex-col items-start gap-2 pl-12">
                  <div className="flex items-center gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Send only if label(s) •
                    </span>
                    <span className="font-['Inter'] text-[11px] font-[500] leading-[16px] text-subtext-color">
                      You can select multiple labels
                    </span>
                    <SubframeCore.Tooltip.Provider>
                      <SubframeCore.Tooltip.Root>
                        <SubframeCore.Tooltip.Trigger asChild={true}>
                          <SubframeCore.Icon
                            className="text-caption font-caption text-neutral-400"
                            name="FeatherHelpCircle"
                          />
                        </SubframeCore.Tooltip.Trigger>
                        <SubframeCore.Tooltip.Portal>
                          <SubframeCore.Tooltip.Content
                            side="top"
                            align="center"
                            sideOffset={4}
                            asChild={true}
                          >
                            <Tooltip>
                              Message will only send if the conversation matches
                              the selected label(s) condition
                            </Tooltip>
                          </SubframeCore.Tooltip.Content>
                        </SubframeCore.Tooltip.Portal>
                      </SubframeCore.Tooltip.Root>
                    </SubframeCore.Tooltip.Provider>
                  </div>
                  <div className="flex w-full items-center gap-2">
                    <Select
                      className="h-auto grow shrink-0 basis-0"
                      label=""
                      placeholder="Condition"
                      helpText=""
                      value={conditionTwoOperator}
                      onValueChange={(value) => setConditionTwoOperator(value)}
                    >
                      <Select.Item value="include">is</Select.Item>
                      <Select.Item value="not">
                        does not include
                      </Select.Item>
                    </Select>
                    <Select
                      className="h-auto grow shrink-0 basis-0"
                      label=""
                      placeholder="Select Label(s)"
                      helpText=""
                      value={undefined}
                      onValueChange={(value) => toggleConditionLabel(value)}
                    >
                      {availableLabels.map((label) => (
                        <Select.Item key={label.id} value={label.label}>
                          {label.label}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div className="flex w-full max-w-[384px] flex-wrap items-start gap-1 rounded-md border border-solid border-neutral-border bg-neutral-50 px-1 py-1">
                    {selectedConditionLabels.map((labelName) => (
                      <CategoryLabel
                        key={labelName}
                        className="w-auto cursor-pointer"
                        text={labelName}
                        onClick={() => toggleConditionLabel(labelName)}
                      />
                    ))}
                  </div>
                  {conditionTwoOperator === "include" && (
                    <div className="flex w-full max-w-[384px] flex-col items-start gap-2 rounded-md border border-solid border-brand-100 bg-brand-50 pl-4 pr-3 py-3">
                      <div className="flex w-full items-center gap-2">
                        <SubframeCore.Icon
                          className="text-heading-3 font-heading-3 text-brand-800"
                          name="FeatherInfo"
                        />
                        <div className="flex grow shrink-0 basis-0 flex-col items-start">
                          <span className="w-full whitespace-pre-wrap text-caption font-caption text-brand-800">
                            {
                              "If the conversation gets a new label from your selection after scheduling, the message will not be sent."
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {conditionTwoOperator === "not" && (
                    <div className="flex w-full max-w-[384px] flex-col items-start gap-2 rounded-md border border-solid border-brand-100 bg-brand-50 pl-4 pr-3 py-3">
                      <div className="flex w-full items-center gap-2">
                        <SubframeCore.Icon
                          className="text-heading-3 font-heading-3 text-brand-800"
                          name="FeatherInfo"
                        />
                        <div className="flex grow shrink-0 basis-0 flex-col items-start">
                          <span className="w-full whitespace-pre-wrap text-caption font-caption text-brand-800">
                            {
                              "Messages will only send only if label does not include selected Label(s)"
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-2 rounded-b-lg border-t border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <Button
            className="mobile:h-8 mobile:grow mobile:shrink-0 mobile:basis-0"
            variant="neutral-secondary"
            onClick={() => setIsScheduleMessagePopupOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className="mobile:h-8 mobile:grow mobile:shrink-0 mobile:basis-0"
            icon="FeatherCalendar"
            onClick={handleScheduleSubmit}
          >
            Schedule Message
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
}

export default ScheduleMessage;